import {SocialAuthIntegration} from '@practicegenius/domain/src/data/integrations';
import {OauthAuthorizationTokens} from '@practicegenius/register-account/src/data/types';

import {
  OauthAuthorizationTokens$Server,
  UserLoginPayload,
} from '../types/loginTypes';
import {sitesHttpClient} from './AuthApiProvider';
import {coreApiClientNoAuth} from './httpClientsJs';
import {CrappyValidationException} from './exceptions';

function incomingUserLoginPayload(
  incomingData: UserLoginPayload,
): UserLoginPayload {
  const {redirect, ...otherData} = incomingData;

  /**
   * Login unfortunately sends down either a fully qualified url with
   * protocol, or a relative url that may be missing the "/" prefix.
   */
  const transformedRedirect = /^http/.test(redirect)
    ? redirect
    : `/${redirect}`.replace('//', '/');

  return {
    ...otherData,
    redirect: transformedRedirect,
  };
}

export async function loginWithCredentials(
  login: string,
  password: string,
  csrf_token: string,
  recaptcha_token: string | null,
  remember_me = false,
  platform: string,
): Promise<UserLoginPayload> {
  const response = await sitesHttpClient._unsafe.post(
    '/login',
    {
      login,
      password,
      remember_me: Number(remember_me),
      platform,
      recaptcha_token,
    },
    {
      headers: {
        'X-CSRF-TOKEN': csrf_token,
      },
    },
  );

  if (!response.data.success) {
    throw new CrappyValidationException(response.data);
  }

  return incomingUserLoginPayload(response.data);
}

export async function loginWithSocialToken(
  provider: SocialAuthIntegration,
  social_access_token: string,
): Promise<UserLoginPayload> {
  const response = await sitesHttpClient._unsafe.post('/login', {
    provider,
    social_access_token,
  });

  if (!response.data.success) {
    throw new CrappyValidationException(response.data);
  }

  return incomingUserLoginPayload(response.data);
}

export function parseAuthorizationTokens(
  data: OauthAuthorizationTokens$Server,
): OauthAuthorizationTokens {
  return {
    tokenType: data.token_type,
    expiresIn: data.expires_in,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  };
}

function serializeAuthorizationTokens(
  tokens: OauthAuthorizationTokens,
): OauthAuthorizationTokens$Server {
  return {
    token_type: tokens.tokenType,
    expires_in: tokens.expiresIn,
    access_token: tokens.accessToken,
    refresh_token: tokens.refreshToken,
  };
}

export async function loginWithOauthAccessTokens(
  tokens: OauthAuthorizationTokens,
  optionalData: Partial<{
    practice_record_id: number;
    remember_me: boolean;
  }> = {remember_me: true},
): Promise<UserLoginPayload> {
  const response = await sitesHttpClient._unsafe.post('/login', {
    ...optionalData,
    remember_me: Number(optionalData.remember_me),
    ...serializeAuthorizationTokens(tokens),
  });

  if (!response.data.success) {
    throw new CrappyValidationException(response.data);
  }

  return incomingUserLoginPayload(response.data);
}

/**
 * POST /global_users/forgot_username email request
 */
export async function forgotUsername(data: {email: string}): Promise<{}> {
  return coreApiClientNoAuth._unsafe.post(
    '/global_users/forgot_username',
    data,
  );
}

/**
 * POST /global_users/forgot_password email request
 */
export async function forgotPassword(data: {
  username_or_email: string;
}): Promise<void> {
  return coreApiClientNoAuth._unsafe.post(
    '/global_users/forgot_password',
    data,
  );
}

/**
 * POST /global_users/reset_password/:token email request
 */
export async function resetPassword(data: {
  password: string;
  password_confirmation: string;
  token: string;
}): Promise<OauthAuthorizationTokens> {
  const {
    data: tokenData,
  }: {
    data: OauthAuthorizationTokens$Server;
  } = await sitesHttpClient._unsafe.post('/login/renewpassword', data);

  return parseAuthorizationTokens(tokenData);
}
